/**
 * Brand Onboarding Wizard
 * ========================
 *
 * 5-step guided brand calibration for new organizations.
 * Progress is persisted to localStorage so users can resume after closing the browser.
 *
 * Steps:
 * 1. Identity — company name, industry, value proposition
 * 2. Voice    — tone adjectives + positive/negative phrase examples
 * 3. Proof    — case studies and verified claims
 * 4. Voice calibration — paste real brand copy to train the voice model
 * 5. First generation — kick off the first content piece
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { submitBrandCalibration } from '../lib/api'
import type { BrandCalibrationPayload } from '../lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Step1Data {
  company_name: string
  industry: string
  tagline: string
  value_proposition: string
}

interface Step2Data {
  tone_adjectives: string[]
  positive_phrases: string[]
  negative_phrases: string[]
}

interface ProofPoint {
  claim: string
  client: string
  year: string
  source: string
  verified: boolean
}

interface Step3Data {
  proof_points: ProofPoint[]
}

interface Step4Data {
  calibration_samples: string[]
}

interface WizardData {
  step1: Step1Data
  step2: Step2Data
  step3: Step3Data
  step4: Step4Data
  primary_channel: string
  primary_persona_id: string
}

const STORAGE_KEY_PREFIX = 'brand_onboarding_draft'

const STEP_LABELS = [
  'Identidad',
  'Voz de marca',
  'Proof points',
  'Calibración de voz',
  'Primera generación',
]

const TONE_OPTIONS = [
  'Directo', 'Confiado', 'Técnico', 'Accesible', 'Formal',
  'Cercano', 'Disruptivo', 'Aspiracional', 'Pragmático', 'Analítico',
]

const INDUSTRY_OPTIONS = [
  'Consultoría estratégica', 'Tecnología', 'Finanzas', 'Salud',
  'Manufactura', 'Retail', 'Educación', 'Real estate', 'Otro',
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadDraft(key: string): Partial<WizardData> {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveDraft(key: string, data: Partial<WizardData>) {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch { /* ignore */ }
}

function clearDraft(key: string) {
  try {
    localStorage.removeItem(key)
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const DEFAULT_STEP1: Step1Data = { company_name: '', industry: '', tagline: '', value_proposition: '' }
const DEFAULT_STEP2: Step2Data = { tone_adjectives: [], positive_phrases: ['', '', ''], negative_phrases: ['', '', ''] }
const DEFAULT_STEP3: Step3Data = { proof_points: [] }
const DEFAULT_STEP4: Step4Data = { calibration_samples: ['', '', ''] }

// ---------------------------------------------------------------------------
// Progress indicator
// ---------------------------------------------------------------------------

function StepProgress({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1
        const isCompleted = stepNum < current
        const isActive = stepNum === current
        return (
          <div key={stepNum} className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-emerald-500 text-white'
                    : isActive
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {isCompleted ? '✓' : stepNum}
              </div>
              <span
                className={`text-xs mt-1 whitespace-nowrap ${
                  isActive ? 'text-indigo-600 font-medium' : 'text-slate-400'
                }`}
              >
                {label}
              </span>
            </div>
            {idx < total - 1 && (
              <div
                className={`flex-1 h-0.5 mt-[-14px] mx-1 ${
                  isCompleted ? 'bg-emerald-400' : 'bg-slate-200'
                }`}
                style={{ minWidth: '24px' }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

function Step1Form({ data, onChange }: { data: Step1Data; onChange: (d: Step1Data) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Nombre de la empresa *
        </label>
        <input
          type="text"
          value={data.company_name}
          onChange={(e) => onChange({ ...data, company_name: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Nombre de tu marca"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Industria *
        </label>
        <select
          value={data.industry}
          onChange={(e) => onChange({ ...data, industry: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Selecciona una industria</option>
          {INDUSTRY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Tagline (opcional)
        </label>
        <input
          type="text"
          value={data.tagline}
          onChange={(e) => onChange({ ...data, tagline: e.target.value })}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Describe el valor principal que ofrece tu marca"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Propuesta de valor *
        </label>
        <textarea
          value={data.value_proposition}
          onChange={(e) => onChange({ ...data, value_proposition: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          placeholder="Describe en 2-3 párrafos qué hace tu empresa y qué la diferencia de la competencia"
        />
      </div>
    </div>
  )
}

function Step2Form({ data, onChange }: { data: Step2Data; onChange: (d: Step2Data) => void }) {
  const toggleAdjective = useCallback((adj: string) => {
    const current = data.tone_adjectives
    const updated = current.includes(adj)
      ? current.filter((a) => a !== adj)
      : current.length < 5 ? [...current, adj] : current
    onChange({ ...data, tone_adjectives: updated })
  }, [data, onChange])

  const updatePhrase = (field: 'positive_phrases' | 'negative_phrases', idx: number, val: string) => {
    const arr = [...data[field]]
    arr[idx] = val
    onChange({ ...data, [field]: arr })
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Adjetivos de tono (selecciona hasta 5) *
        </label>
        <div className="flex flex-wrap gap-2">
          {TONE_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggleAdjective(opt)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                data.tone_adjectives.includes(opt)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Frases que SÍ representan tu marca *
        </label>
        <p className="text-xs text-slate-400 mb-2">
          Escribe 3 ejemplos de frases o afirmaciones que uses habitualmente
        </p>
        {data.positive_phrases.map((phrase, idx) => (
          <input
            key={idx}
            type="text"
            value={phrase}
            onChange={(e) => updatePhrase('positive_phrases', idx, e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={`Frase positiva ${idx + 1}`}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Frases que NO representan tu marca *
        </label>
        <p className="text-xs text-slate-400 mb-2">
          Frases de consultoría genérica o tono que deseas evitar
        </p>
        {data.negative_phrases.map((phrase, idx) => (
          <input
            key={idx}
            type="text"
            value={phrase}
            onChange={(e) => updatePhrase('negative_phrases', idx, e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={`Frase a evitar ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

function Step3Form({ data, onChange }: { data: Step3Data; onChange: (d: Step3Data) => void }) {
  const addProofPoint = () => {
    onChange({
      proof_points: [
        ...data.proof_points,
        { claim: '', client: '', year: '', source: '', verified: false },
      ],
    })
  }

  const updatePP = (idx: number, field: keyof ProofPoint, value: string | boolean) => {
    const updated = data.proof_points.map((pp, i) =>
      i === idx ? { ...pp, [field]: value } : pp
    )
    onChange({ proof_points: updated })
  }

  const removePP = (idx: number) => {
    onChange({ proof_points: data.proof_points.filter((_, i) => i !== idx) })
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Agrega los datos y resultados verificables que tu marca puede respaldar. 
        Estos se usarán para verificar que el contenido generado no invente métricas.
      </p>
      {data.proof_points.map((pp, idx) => (
        <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-start">
            <span className="text-sm font-medium text-slate-600">Proof point {idx + 1}</span>
            <button
              type="button"
              onClick={() => removePP(idx)}
              className="text-slate-400 hover:text-red-500 text-xs"
            >
              Eliminar
            </button>
          </div>
          <input
            type="text"
            value={pp.claim}
            onChange={(e) => updatePP(idx, 'claim', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Reducción del 40% en tiempo de decisiones estratégicas"
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              value={pp.client}
              onChange={(e) => updatePP(idx, 'client', e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Cliente (puede ser anónimo)"
            />
            <input
              type="text"
              value={pp.year}
              onChange={(e) => updatePP(idx, 'year', e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Año"
            />
            <input
              type="text"
              value={pp.source}
              onChange={(e) => updatePP(idx, 'source', e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Fuente interna"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={pp.verified}
              onChange={(e) => updatePP(idx, 'verified', e.target.checked)}
              className="rounded border-slate-300 text-indigo-600"
            />
            Dato verificado
          </label>
        </div>
      ))}
      <button
        type="button"
        onClick={addProofPoint}
        className="w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
      >
        + Agregar proof point
      </button>
    </div>
  )
}

function Step4Form({ data, onChange }: { data: Step4Data; onChange: (d: Step4Data) => void }) {
  const updateSample = (idx: number, val: string) => {
    const arr = [...data.calibration_samples]
    arr[idx] = val
    onChange({ calibration_samples: arr })
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Pega 2-3 fragmentos de texto que ejemplifiquen perfectamente la voz de tu marca.
        Pueden ser posts publicados, párrafos de propuestas exitosas, o emails que reflejen tu estilo.
      </p>
      {data.calibration_samples.map((sample, idx) => (
        <div key={idx}>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Fragmento {idx + 1} {idx === 0 ? '*' : '(opcional)'}
          </label>
          <textarea
            value={sample}
            onChange={(e) => updateSample(idx, e.target.value)}
            rows={5}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none text-sm"
            placeholder={`Pega aquí un fragmento representativo de tu voz de marca${idx === 0 ? ' (requerido)' : ''}`}
          />
          <p className="text-xs text-slate-400 mt-1">
            {sample.length} caracteres
          </p>
        </div>
      ))}
    </div>
  )
}

function Step5Form({ onGenerate }: { onGenerate: () => void }) {
  return (
    <div className="text-center space-y-6 py-8">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <span className="text-3xl">✓</span>
      </div>
      <div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">
          Tu marca está lista
        </h3>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Hemos configurado tu identidad de marca, voz y proof points.
          El sistema analizará tus muestras de texto en segundo plano para calibrar
          el estilo de escritura de forma continua.
        </p>
      </div>
      <button
        type="button"
        onClick={onGenerate}
        className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
      >
        Generar mi primer contenido
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

function validateStep1(d: Step1Data): string | null {
  if (!d.company_name.trim()) return 'El nombre de la empresa es requerido'
  if (!d.industry) return 'Selecciona una industria'
  if (!d.value_proposition.trim() || d.value_proposition.trim().length < 50) {
    return 'La propuesta de valor debe tener al menos 50 caracteres'
  }
  return null
}

function validateStep2(d: Step2Data): string | null {
  if (d.tone_adjectives.length < 2) return 'Selecciona al menos 2 adjetivos de tono'
  if (!d.positive_phrases[0]?.trim()) return 'Agrega al menos una frase positiva'
  if (!d.negative_phrases[0]?.trim()) return 'Agrega al menos una frase a evitar'
  return null
}

function validateStep4(d: Step4Data): string | null {
  if (!d.calibration_samples[0]?.trim() || d.calibration_samples[0].trim().length < 100) {
    return 'El primer fragmento de calibración debe tener al menos 100 caracteres'
  }
  return null
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function BrandOnboardingPage() {
  const { loading: authLoading, user } = useAuth()
  const navigate = useNavigate()

  const storageKey = user?.id
    ? `${STORAGE_KEY_PREFIX}_${user.id}`
    : STORAGE_KEY_PREFIX

  const draft = loadDraft(storageKey)

  const [currentStep, setCurrentStep] = useState(1)
  const [step1, setStep1] = useState<Step1Data>({ ...DEFAULT_STEP1, ...(draft.step1 || {}) })
  const [step2, setStep2] = useState<Step2Data>({ ...DEFAULT_STEP2, ...(draft.step2 || {}) })
  const [step3, setStep3] = useState<Step3Data>({ ...DEFAULT_STEP3, ...(draft.step3 || {}) })
  const [step4, setStep4] = useState<Step4Data>({ ...DEFAULT_STEP4, ...(draft.step4 || {}) })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const persistDraft = useCallback((updates: Partial<WizardData>) => {
    const current = { step1, step2, step3, step4 }
    saveDraft(storageKey, { ...current, ...updates })
  }, [step1, step2, step3, step4, storageKey])

  const handleNext = async () => {
    setError(null)

    let validationError: string | null = null
    if (currentStep === 1) validationError = validateStep1(step1)
    if (currentStep === 2) validationError = validateStep2(step2)
    if (currentStep === 4) validationError = validateStep4(step4)

    if (validationError) {
      setError(validationError)
      return
    }

    if (currentStep === 4) {
      // Submit before advancing to step 5
      await handleSubmit()
      return
    }

    setCurrentStep((s) => s + 1)
  }

  const handleBack = () => {
    setError(null)
    setCurrentStep((s) => Math.max(1, s - 1))
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const payload: BrandCalibrationPayload = {
        step1,
        step2,
        step3: {
          proof_points: step3.proof_points.map((pp) => ({
            ...pp,
            year: parseInt(pp.year) || null,
          })),
        },
        step4: {
          calibration_samples: step4.calibration_samples.filter((s) => s.trim().length > 0),
        },
        primary_channel: 'linkedin',
        primary_persona_id: null,
      }

      await submitBrandCalibration(payload)
      clearDraft(storageKey)
      setCurrentStep(5)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar la configuración'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  if (authLoading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Configuración de marca</h1>
        </div>
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Configura tu marca</h1>
        <p className="text-slate-500 mt-1">
          Completa los 5 pasos para que el sistema aprenda la voz y los estándares de tu marca.
        </p>
      </div>

      <StepProgress current={currentStep} total={5} />

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          {STEP_LABELS[currentStep - 1]}
        </h2>

        {currentStep === 1 && (
          <Step1Form data={step1} onChange={(d) => { setStep1(d); persistDraft({ step1: d }) }} />
        )}
        {currentStep === 2 && (
          <Step2Form data={step2} onChange={(d) => { setStep2(d); persistDraft({ step2: d }) }} />
        )}
        {currentStep === 3 && (
          <Step3Form data={step3} onChange={(d) => { setStep3(d); persistDraft({ step3: d }) }} />
        )}
        {currentStep === 4 && (
          <Step4Form data={step4} onChange={(d) => { setStep4(d); persistDraft({ step4: d }) }} />
        )}
        {currentStep === 5 && (
          <Step5Form onGenerate={() => navigate('/content')} />
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {currentStep < 5 && (
          <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={submitting}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <LoadingSpinner size="sm" />}
              {currentStep === 4 ? 'Guardar y continuar' : 'Siguiente'}
            </button>
          </div>
        )}
      </div>

      {currentStep < 4 && (
        <p className="text-xs text-slate-400 text-center mt-4">
          Tu progreso se guarda automáticamente en este navegador.
        </p>
      )}
    </div>
  )
}
